/**
 * Landing v3 — the phone's first interaction (Figma 116:885 idle → 116:972
 * transition → 116:1100 organized; user 2026-09-29). The hero is pinned like
 * the desktop's stage while the first feature card rises over it: the
 * study-tool chips drift on their own, then — as the reader scrolls — organize
 * into the rail beside that card, while Ka glides from his corner to the
 * centre to speak his line, and back (landing-phone-ka).
 *
 * Two mechanisms, never mixed:
 * - DRIFT: each chip's inner face loops a slow ellipse (12–20s) run by the
 *   browser's compositor (Web Animations, transform only). Time-based, it never
 *   reads the scroll. It runs only while the hero is at rest and the tab is
 *   visible, never under reduced motion, and eases back to rest the moment the
 *   reader scrolls.
 * - ORGANIZE: each chip's outer wrapper travels from its hero spot to its rail
 *   slot, scrubbed by the scroll both ways. It computes only while the reader
 *   scrolls through the first screen and a half.
 *
 * The hero is laid out on the frames' own screen — the iPhone 13's 390×723
 * under Safari's bars — and scaled to fit, so every phone sees the frame.
 */
import {
  motion,
  type MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from 'framer-motion';
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { clamp01, easeInOutCubic, LAYOUT_SPRING, TrustedPill } from './landing-chrome';
import {
  INTRO_POP_ORDER,
  LANDING_FEATURES,
  type LandingFeature,
} from './landing-features';
import { EASE_OUT } from './landing-ka';
import { KA_LINES, TriggeredLine } from './landing-lines';
import {
  type Drift,
  driftKeyframes,
  fitBox,
  HERO_FRAME as FRAME,
  type HeroBox,
} from './landing-phone-layout';

// ── The frames' geometry ─────────────────────────────────────────────────────

/** A hero chip (Figma 116:936): 11.8px around a 28.4px icon, 1.2px border. */
const HERO_CHIP = 54;
/** A rail chip (Figma 116:1111): 8.2px around a 19.7px icon. */
export const RAIL_CHIP = 36;
/** Frame 116:972: Ka centred over his line, and where the rising card's top
 *  is at that moment. */
const KA_CENTRE = { x: 194, y: 192, size: 80 } as const;
const LINE = { top: 252, bottom: 366, width: 344 } as const;
const CARD_AT_LINE = 446;
/**
 * Each chip's centre and tilt on the hero (frame 116:885, less Safari's 46px
 * status bar), and its drift. Eight are the frame's; mind maps and the oral
 * exam are not in it, so they wait just past the right edge, and the planner
 * sits past the left one, as the frame has it — their drift brings them
 * peeking in and out.
 */
const SPOTS: Record<string, { x: number; y: number; rotate: number; drift: Drift }> = {
  quizzes: {
    x: 5,
    y: 164,
    rotate: -14.55,
    drift: { ax: 26, ay: 14, r: 4, s: 17, phase: 0.1 },
  },
  summaries: {
    x: 133.2,
    y: 164,
    rotate: 12.99,
    drift: { ax: 16, ay: 12, r: 3, s: 14, phase: 0.55 },
  },
  flashcards: {
    x: 258.8,
    y: 161,
    rotate: -9.21,
    drift: { ax: 18, ay: 14, r: 3, s: 15, phase: 0.3 },
  },
  spacedRepetition: {
    x: 375.8,
    y: 154,
    rotate: -15,
    drift: { ax: 24, ay: 12, r: 4, s: 19, phase: 0.8 },
  },
  mindMaps: {
    x: 436,
    y: 236,
    rotate: -8,
    drift: { ax: 34, ay: 16, r: 4, s: 20, phase: 0.2 },
  },
  examPlanner: {
    x: -53.8,
    y: 538,
    rotate: 16.24,
    drift: { ax: 34, ay: 14, r: 4, s: 18, phase: 0.65 },
  },
  caseStudies: {
    x: 65.6,
    y: 554,
    rotate: 3.55,
    drift: { ax: 16, ay: 12, r: 3, s: 13, phase: 0.4 },
  },
  transcriber: {
    x: 200.7,
    y: 560,
    rotate: -11.34,
    drift: { ax: 18, ay: 14, r: 3, s: 16, phase: 0.05 },
  },
  imageOcclusion: {
    x: 334.4,
    y: 545,
    rotate: 8.43,
    drift: { ax: 20, ay: 12, r: 3, s: 12, phase: 0.7 },
  },
  oralExam: {
    x: 430,
    y: 470,
    rotate: 10,
    drift: { ax: 34, ay: 16, r: 4, s: 19, phase: 0.45 },
  },
};

// ── The hero's box ───────────────────────────────────────────────────────────

export type { HeroBox };

export function useHeroBox(stageRef: RefObject<HTMLDivElement | null>): HeroBox {
  const [box, setBox] = useState<HeroBox>(() =>
    fitBox(
      typeof window === 'undefined' ? FRAME.w : window.innerWidth,
      typeof window === 'undefined' ? FRAME.h : window.innerHeight,
    ),
  );
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => {
      const next = fitBox(stage.clientWidth, stage.clientHeight);
      setBox((prev) => (prev.w === next.w && prev.h === next.h ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [stageRef]);
  return box;
}

// ── The timeline ─────────────────────────────────────────────────────────────

/**
 * The first interaction, from the scroll and the first card's place in the
 * page (`cardTop`, document px; the card rises natively). `y2` is the scroll
 * of frame 116:972 (the card's top at the frame's 446px): by then the hero
 * copy has gone, Ka has his line and the chips sit in the rail.
 */
export function useHeroTimeline({
  scrollY,
  box,
  cardTop,
  layoutTick,
}: {
  scrollY: MotionValue<number>;
  box: HeroBox;
  cardTop: RefObject<number>;
  layoutTick: MotionValue<number>;
}) {
  const boxRef = useRef(box);
  const boxTick = useMotionValue(0);
  useLayoutEffect(() => {
    boxRef.current = box;
    boxTick.set(boxTick.get() + 1);
  }, [box, boxTick]);

  const marks = () => {
    layoutTick.get();
    boxTick.get();
    const b = boxRef.current;
    const c0 = cardTop.current || b.h * 1.2;
    return {
      c0,
      y2: Math.max(1, c0 - (b.oy + CARD_AT_LINE * b.k)),
      lineBottom: b.oy + LINE.bottom * b.k,
    };
  };
  /** The hero copy lifts away over the first stretch. */
  const heroOut = useTransform(() => clamp01(scrollY.get() / (0.3 * marks().y2)));
  /** The brand pill hands over to the bar. */
  const handoff = useTransform(() => {
    const { y2 } = marks();
    return clamp01((scrollY.get() - 0.1 * y2) / (0.3 * y2));
  });
  /** 0 on the hero, 1 in the rail — linear in scroll. */
  const organize = useTransform(() => {
    const { y2 } = marks();
    return clamp01((scrollY.get() - 0.1 * y2) / (0.9 * y2));
  });
  /** Ka's line: in once Ka is up, out before the rising card reaches it. */
  const lineOpacity = useTransform(() => {
    const { y2, c0, lineBottom } = marks();
    const y = scrollY.get();
    const shown = clamp01((y - 0.28 * y2) / (0.14 * y2));
    const clear = clamp01((c0 - y - (lineBottom + 16)) / 48);
    return shown * clear;
  });

  const [atRest, setAtRest] = useState(true);
  const [kaCentred, setKaCentred] = useState(false);
  const [lineFired, setLineFired] = useState(false);
  const update = () => {
    const { y2, c0, lineBottom } = marks();
    const y = scrollY.get();
    setAtRest(y < 4);
    // Up for his line once the headline is going, back to his corner before
    // the rising card reaches it.
    setKaCentred(y >= 0.25 * y2 && c0 - y > lineBottom + 40);
    if (lineOpacity.get() > 0.3) setLineFired(true);
  };
  useMotionValueEvent(scrollY, 'change', update);
  useMotionValueEvent(layoutTick, 'change', update);
  useEffect(update, []);

  return { heroOut, handoff, organize, lineOpacity, atRest, kaCentred, lineFired };
}

/** Ka's spot over his line (frame 116:972), on this screen. */
export function heroKaSpot(box: HeroBox) {
  const size = KA_CENTRE.size * box.k;
  return {
    x: box.ox + KA_CENTRE.x * box.k - size / 2,
    y: box.oy + KA_CENTRE.y * box.k - size / 2,
    size,
  };
}

// ── The stage ────────────────────────────────────────────────────────────────

/**
 * The pinned hero (frame 116:885): the headline and the trusted pill, and in
 * their place Ka's line (116:972). The first card rises over it (the pile
 * comes next in the page, pulled up by a screen), so it holds still until
 * that card has landed under the bar.
 */
export function PhoneHeroStage({
  stageRef,
  box,
  heroOut,
  lineOpacity,
  lineFired,
  users,
  reduceMotion,
}: {
  stageRef: RefObject<HTMLDivElement | null>;
  box: HeroBox;
  heroOut: MotionValue<number>;
  lineOpacity: MotionValue<number>;
  lineFired: boolean;
  users: number | null;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  const heroOpacity = useTransform(heroOut, (v) => 1 - v);
  const heroY = useTransform(heroOut, (v) => (reduceMotion ? 0 : -40 * v));
  const segments = KA_LINES[0].rows
    .flat()
    .map((seg) => ({ text: t(seg.key), hl: seg.hl }));
  return (
    <div className="lv3-hero-runway w-full">
      <div
        ref={stageRef}
        className="sticky top-0 h-screen w-full overflow-clip supports-[height:100svh]:h-[100svh]">
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: FRAME.w,
            height: FRAME.h,
            transform: `translate(${box.ox}px, ${box.oy}px) scale(${box.k})`,
          }}>
          <motion.div
            style={{ opacity: heroOpacity, y: heroY }}
            className="absolute inset-x-0 top-[269px] flex flex-col items-center gap-5">
            <motion.h1
              initial={reduceMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease: EASE_OUT }}
              className="w-[315px] text-center text-[40px] font-bold leading-[1.2] text-secondary-800">
              {t('landingV3.headline.pre')}{' '}
              <span className="text-primary-600">{t('landingV3.headline.ai')}</span>{' '}
              {t('landingV3.headline.post')} {t('landingV3.headline.line2')}
            </motion.h1>
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25, ease: EASE_OUT }}>
              <TrustedPill users={users} />
            </motion.div>
          </motion.div>
          <motion.div
            style={{ opacity: lineOpacity, top: LINE.top, width: LINE.width }}
            className="absolute left-1/2 -ml-[172px]">
            <TriggeredLine
              as="p"
              segments={segments}
              arrived={lineFired}
              delay={0.1}
              reduceMotion={reduceMotion}
              className="text-center text-[32px] font-bold leading-[38px] text-secondary-800"
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// ── The chips ────────────────────────────────────────────────────────────────

/** The rail's slots, measured by the pile: the rail's left edge and its top
 *  in the page's flow (document px), and each slot's centre inside it. */
export type RailGeometry = {
  left: number;
  top: number;
  /** The rail's scale (below 1 beside a card shorter than it). */
  scale: number;
  slots: { x: number; y: number }[];
};
export const NO_RAIL: RailGeometry = { left: 0, top: 0, scale: 1, slots: [] };

/**
 * A chip's ambient drift on its face element — the compositor runs it, so no
 * script runs per frame. Off (settling back to rest in 0.45s) whenever
 * `running` drops; paused while the tab is hidden.
 */
function useAmbientDrift(
  ref: RefObject<HTMLElement | null>,
  drift: Drift,
  running: boolean,
) {
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !running || typeof el.animate !== 'function') return;
    // The first loop starts part-way (hidden by the chips' pop-in), so the
    // chips never move in step; a restart after a scroll begins at rest.
    const loop = el.animate(driftKeyframes(drift), {
      duration: drift.s * 1000,
      iterations: Infinity,
      easing: 'linear',
      delay: started.current ? 0 : -drift.phase * drift.s * 1000,
    });
    started.current = true;
    const onVisibility = () => (document.hidden ? loop.pause() : loop.play());
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      const current = getComputedStyle(el).transform;
      loop.cancel();
      if (current && current !== 'none') {
        el.animate([{ transform: current }, { transform: 'none' }], {
          duration: 450,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        });
      }
    };
  }, [ref, drift, running]);
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** Between one chip starting for the rail and the next (fractions of the
 *  organize step). */
const LAND_STAGGER = 0.025;

function OrganizingChip({
  feature,
  index,
  box,
  organize,
  rail,
  scrollY,
  layoutTick,
  drifting,
  reduceMotion,
  onOpen,
}: {
  feature: LandingFeature;
  index: number;
  box: HeroBox;
  organize: MotionValue<number>;
  rail: RefObject<RailGeometry>;
  scrollY: MotionValue<number>;
  layoutTick: MotionValue<number>;
  drifting: boolean;
  reduceMotion: boolean;
  onOpen: (index: number) => void;
}) {
  const { t } = useTranslation();
  const face = useRef<HTMLSpanElement>(null);
  const spot = SPOTS[feature.id];
  useAmbientDrift(face, spot.drift, drifting);

  const hero = { x: box.ox + spot.x * box.k, y: box.oy + spot.y * box.k };
  const target = () => {
    layoutTick.get();
    const r = rail.current;
    const slot = r.slots[index];
    if (!slot) return hero;
    // The rail rises with the card: its slot is where the flow has it now.
    return { x: r.left + slot.x, y: r.top + slot.y - scrollY.get() };
  };
  // Each chip lands in turn, top slot first, so the rail fills in order; all
  // are in by 92%, before the hand-over to the rail's own chips.
  const start = index * LAND_STAGGER;
  const p = () =>
    reduceMotion ? 0 : easeInOutCubic(clamp01((organize.get() - start) / (0.92 - start)));
  const x = useTransform(() => lerp(hero.x, target().x, p()) - HERO_CHIP / 2);
  const y = useTransform(() => lerp(hero.y, target().y, p()) - HERO_CHIP / 2);
  const scale = useTransform(() =>
    lerp(box.k, (RAIL_CHIP * rail.current.scale) / HERO_CHIP, p()),
  );
  const rotate = useTransform(() => lerp(spot.rotate, 0, p()));
  const opacity = useTransform(() => {
    const o = organize.get();
    // Reduced motion: no flight — the chip fades where it is, and the rail's
    // own chip appears when the card brings it.
    if (reduceMotion) return 1 - clamp01((o - 0.15) / 0.3);
    // Dimmed as the rail dims it (only the first card's chip is lit), then
    // handed to the rail's own chip in the last few percent.
    const dim = index === 0 ? 1 : lerp(1, 0.3, p());
    return dim * (1 - clamp01((o - 0.94) / 0.06));
  });
  const pointerEvents = useTransform(() => (organize.get() < 0.9 ? 'auto' : 'none'));
  const pop = Math.max(0, INTRO_POP_ORDER.indexOf(feature.id));
  const Icon = feature.icon;

  return (
    <motion.button
      type="button"
      onClick={() => onOpen(index)}
      aria-label={t(feature.badgeKey)}
      style={{ x, y, scale, rotate, opacity, pointerEvents }}
      // Its own layer: the flight moves it every frame, and its tinted shadow
      // is a filter — repainted in place, it cost frames.
      className="absolute left-0 top-0 h-[54px] w-[54px] will-change-transform">
      {/* The one-off pop-in, between the scroll's transform and the drift's. */}
      <motion.span
        initial={reduceMotion ? false : { opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.35 + pop * 0.05, ...LAYOUT_SPRING }}
        className="block h-full w-full">
        <span
          ref={face}
          className={cn(
            'flex h-full w-full items-center justify-center rounded-[16.5px] border-[1.2px]',
            feature.chipClass,
            feature.shadowClass,
          )}>
          <Icon className={cn('h-7 w-7', feature.iconClass)} />
        </span>
      </motion.span>
    </motion.button>
  );
}

/**
 * The chips, over the page (they land beside the rising card, so they fly
 * above it). Hidden once they are in the rail — the rail's own chips take
 * over — and clipped to the screen, as the frame crops the ones at its edges.
 */
export function PhoneChipLayer({
  box,
  organize,
  rail,
  scrollY,
  layoutTick,
  atRest,
  reduceMotion,
  onOpen,
}: {
  box: HeroBox;
  organize: MotionValue<number>;
  rail: RefObject<RailGeometry>;
  scrollY: MotionValue<number>;
  layoutTick: MotionValue<number>;
  /** The page sits at its top: the chips drift. */
  atRest: boolean;
  reduceMotion: boolean;
  onOpen: (index: number) => void;
}) {
  const visibility = useTransform(() => (organize.get() >= 1 ? 'hidden' : 'visible'));
  // The drift runs only while the hero is on screen and at rest; the pinned
  // stage is the whole screen then, so at rest is enough.
  const drifting = atRest && !reduceMotion;
  return (
    <motion.div
      aria-hidden={!atRest}
      style={{ visibility }}
      className="pointer-events-none fixed inset-0 z-[15] overflow-clip">
      {LANDING_FEATURES.map((feature, i) => (
        <OrganizingChip
          key={feature.id}
          feature={feature}
          index={i}
          box={box}
          organize={organize}
          rail={rail}
          scrollY={scrollY}
          layoutTick={layoutTick}
          drifting={drifting}
          reduceMotion={reduceMotion}
          onOpen={onOpen}
        />
      ))}
    </motion.div>
  );
}
